import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, ChefHat, AlertCircle, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    carbs: '',
    sugar: '',
    calories: '',
    protein: '',
    prep_time: '',
    difficulty: 'Easy',
    meal_type: 'Breakfast',
    ingredients: '',
    instructions: '',
    image: '',
    tags: ''
  });

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data.recipes);
    } catch (error) {
      showMessage('error', 'Error loading recipes');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...formData,
        carbs: parseInt(formData.carbs),
        sugar: parseInt(formData.sugar),
        calories: parseInt(formData.calories),
        protein: parseInt(formData.protein),
        ingredients: formData.ingredients.split('\n').filter(i => i.trim()),
        instructions: formData.instructions.split('\n').filter(i => i.trim()),
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      await axios.post(
        `${API_URL}/api/admin/recipes`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showMessage('success', 'Recipe added successfully');
      resetForm();
      fetchRecipes();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Error saving recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/admin/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'Recipe deleted successfully');
      fetchRecipes();
    } catch (error) {
      showMessage('error', 'Error deleting recipe');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      carbs: '',
      sugar: '',
      calories: '',
      protein: '',
      prep_time: '',
      difficulty: 'Easy',
      meal_type: 'Breakfast',
      ingredients: '',
      instructions: '',
      image: '',
      tags: ''
    });
    setShowForm(false);
  };

  const difficulties = ['Easy', 'Medium', 'Hard'];
  const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Smoothie', 'Dessert'];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Recipe Management</h2>
            <p className="text-gray-600 mt-1">Add and manage diabetes-friendly recipes</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Recipe
          </Button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`flex items-center space-x-2 p-4 rounded-lg border ${
            message.type === 'success' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {message.text}
            </p>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <Card className="border-blue-200 shadow-lg">
            <CardHeader>
              <CardTitle>Add a Recipe</CardTitle>
              <CardDescription>
                Fill in nutritional information and instructions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Recipe Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meal_type">Meal Type *</Label>
                    <select
                      id="meal_type"
                      value={formData.meal_type}
                      onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {mealTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="carbs">Carbs (g) *</Label>
                    <Input
                      id="carbs"
                      type="number"
                      value={formData.carbs}
                      onChange={(e) => setFormData({ ...formData, carbs: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sugar">Sugar (g) *</Label>
                    <Input
                      id="sugar"
                      type="number"
                      value={formData.sugar}
                      onChange={(e) => setFormData({ ...formData, sugar: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="calories">Calories *</Label>
                    <Input
                      id="calories"
                      type="number"
                      value={formData.calories}
                      onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="protein">Protein (g) *</Label>
                    <Input
                      id="protein"
                      type="number"
                      value={formData.protein}
                      onChange={(e) => setFormData({ ...formData, protein: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prep_time">Prep time</Label>
                    <Input
                      id="prep_time"
                      placeholder="Ex: 30 min"
                      value={formData.prep_time}
                      onChange={(e) => setFormData({ ...formData, prep_time: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="difficulty">Difficulty</Label>
                    <select
                      id="difficulty"
                      value={formData.difficulty}
                      onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {difficulties.map(diff => (
                        <option key={diff} value={diff}>{diff}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">Image URL</Label>
                  <Input
                    id="image"
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ingredients">Ingredients (one per line) *</Label>
                  <Textarea
                    id="ingredients"
                    value={formData.ingredients}
                    onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                    rows={6}
                    placeholder="200g de farine d'amande&#10;2 œufs&#10;100ml de lait d'amande"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="instructions">Instructions (une par ligne) *</Label>
                  <Textarea
                    id="instructions"
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={6}
                    placeholder="Preheat oven to 350°F&#10;Mix dry ingredients&#10;Add eggs and milk"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="low-carb, gluten-free, vegetarian"
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-cyan-600"
                  >
                    {loading ? 'Saving...' : 'Add'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Recipes List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <ChefHat className="w-5 h-5 mr-2" />
              Recipes ({recipes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !recipes.length ? (
              <p className="text-center text-gray-500 py-8">Loading...</p>
            ) : recipes.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No recipes found</p>
            ) : (
              <div className="space-y-3">
                {recipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all"
                  >
                    <div className="flex space-x-4 flex-1">
                      {recipe.image && (
                        <img
                          src={recipe.image}
                          alt={recipe.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{recipe.name}</h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {recipe.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-sm">
                          <span className="text-blue-600 font-semibold">
                            {recipe.carbs}g carbs
                          </span>
                          <span className="text-gray-500">{recipe.calories} cal</span>
                          <span className="text-gray-500">{recipe.meal_type}</span>
                          <span className="text-gray-500">{recipe.difficulty}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(recipe.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminRecipes;
